import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { AppState } from "../types/AppState";
import { ContextProviderProps } from "../../../types/ContextProviderProps";
import { BaseAssetDto } from "../../../types/BaseAssetDto";
import type { MarketDataDto } from "../../../types/MarketDataDto";
import { LocalStorageConstants } from "../../../constants/LocalStorageConstants";
import { dateTimeConversionService } from "../../../services/DateTimeConversionService";
import { portfolioMappers } from "../../../mappers/PortfolioMappers";
import { useAuthContext } from "../../../../screens/authentication/hooks/use-auth/AuthContext";
import { useNavigate } from "react-router-dom";
import { DataProviders } from "../../../types/DataProviders";
import { ViewSectionType } from "../../../../screens/module-selector/types/ViewSectionType";
import { ModuleType } from "../../../../screens/module-selector/types/ModuleType";
import { UserDto } from "../../../../screens/options-table/types/UserDto";
import { createAuthenticatedApiClient } from "../../../services/api/AuthenticatedApiClient";
import { createUserApi } from "../../../services/api/UserApi";
import { createMarketDataApi } from "../../../services/api/MarketDataApi";

const AppContext = createContext<AppState | undefined>(undefined);

export function AppContextProvider({ children }: ContextProviderProps) {
    const { userAuthData, isUserAuthenticated } = useAuthContext();
    
    const [selectedBaseAsset, setSelectedBaseAsset] = useState<BaseAssetDto | null>(null);
    const [selectedExpiration, setSelectedExpiration] = useState<string | null>(null);
    const [marketDataDto, setMarketDataDto] = useState<MarketDataDto | undefined>();
    const [selectedDataProvider, setSelectedDataProvider] = useState<DataProviders | null>(null);
    const [selectedPortfolioBalance, setSelectedPortfolioBalance] = useState<number>(0);
    const [selectedPortfolioVolume, setSelectedPortfolioVolume] = useState<number>(0);
    const [showBalancerPriceLines, setShowBalancerPriceLines] = useState<boolean>(false);
    const [balancerPriceLineDelta, setBalancerPriceLineDelta] = useState<number | null>(null);
    // View section selection
    // todo: ViewSection.Left from user settings?
    const [activeViewSection, setActiveViewSection] = useState<ViewSectionType>(ViewSectionType.Left);    
    const [leftViewSectionModuleType, setLeftViewSectionModuleType] = useState<ModuleType>(ModuleType.PositionsAndOrders);    
    const [rightViewSectionModuleType, setRightViewSectionModuleType] = useState<ModuleType>(ModuleType.BalanceProfile);    
    
    const navigate = useNavigate();
    const apiClient = useMemo(
        () => createAuthenticatedApiClient({ userAuthData, isUserAuthenticated, navigate }),
        [userAuthData, isUserAuthenticated, navigate],
    );
    const userApi = useMemo(() => createUserApi(apiClient), [apiClient]);
    const marketDataApi = useMemo(() => createMarketDataApi(apiClient), [apiClient]);
    
    // SignalR connection handling
    // const connection = new signalR
    //     .HubConnectionBuilder()
    //     .withUrl(`${configurationService.configuration.baseAddress}/spotsHub`)
    //     .build();
    //
    // connection.start()
    //     .then(() => console.log("Connection is started for 'spotsHub'"))
    //     .catch(err => console.log('Error while starting connection: ' + err));
    //
    // connection.on("Update", (value) => {
    //     if (Array.isArray(value)) {
    //         const updates = value as UpdateSpotDto[];
    //         updates.forEach(item => {
    //             console.log(`Symbol: ${item.symbol}, MarkPrice: ${item.markPrice}`);
    //
    //             if (marketDataDto &&
    //                 marketDataDto.BaseAsset.BaseAsset === item.symbol.slice(0, 3)
    //             ) {
    //                 marketDataDto.BaseAsset.Price = item.markPrice;
    //                 setMarketDataDto(marketDataDto);
    //             }
    //         });
    //     } else {
    //         console.warn("Unexpected update format", value);
    //     }
    // });
    
    const getCurrentUser = async (): Promise<UserDto | null> => {
        return await userApi.getCurrent();
    }

    useEffect(() => {
        const initializeCurrentUser = async (): Promise<void> => {
            const currentUser = await userApi.getCurrent();

            if (currentUser !== null) {
                setSelectedDataProvider(currentUser.DataProvider);
            }
        };

        initializeCurrentUser();
    }, [userApi]);
    
    const fetchMarketData = async (
        baseAssetSymbol: string | null,
        expiration: string | null,
        doAfterFetch?: (value: MarketDataDto | null) => void,
    ): Promise<MarketDataDto | null> => {
        const currentUser = selectedDataProvider === null
            ? await getCurrentUser()
            : null;

        if (selectedDataProvider === null && currentUser === null) {
            doAfterFetch?.(null);
            return null;
        }

        const dataProvider = selectedDataProvider ?? currentUser!.DataProvider;
        
        if (selectedDataProvider === null)
            setSelectedDataProvider(dataProvider);
        
        const query = new URLSearchParams({
            baseAssetSymbol: baseAssetSymbol ?? '',
            expireAt: dateTimeConversionService.parseAndConvertDateToShortFormattedString(expiration) ?? '',
            dataProviders: `${dataProvider}`,
        });
        
        let marketDataDto: MarketDataDto | null = null;
        
        marketDataDto = await marketDataApi.getMarketData(query);

        if (marketDataDto === null) {
            doAfterFetch?.(null);
            return null;
        }

        marketDataDto.OptionInfos.forEach(x => {
            const { price, delta, gamma, vega, theta } =
                portfolioMappers.getAssetParameters(x.Type, x.Strike, x.ExpireAt,
                    x.MarkImpliedVolatility, x.RiskFreeInterest, marketDataDto!.UnderlyingPrice);

            x.MarkPrice = price;
            x.Delta = delta;
            x.Gamma = gamma ?? 0;
            x.Vega = vega ?? 0;
            x.Theta = theta ?? 0;
        })

        setMarketDataDto(marketDataDto);

        if (baseAssetSymbol == null)
            setSelectedBaseAsset(marketDataDto.AvailableBaseAssets[0]);
        else if (selectedBaseAsset == null)
            setSelectedBaseAsset(marketDataDto.AvailableBaseAssets.find(x => x.Symbol === baseAssetSymbol) ?? null);

        const newExpiration = marketDataDto.Expirations
            .map(x => dateTimeConversionService.convertDateToLongFormattedString(x))
            .find(x => x.toString() === expiration)?.toString();

        if (newExpiration == null) {
            const value = marketDataDto.Expirations[0].toString();
            setSelectedExpiration(value);
            localStorage.setItem(LocalStorageConstants.SelectedExpirationName, value);
        } else {
            setSelectedExpiration(newExpiration);
        }

        doAfterFetch?.(marketDataDto);
        
        return marketDataDto;
    }
    
    const value = {
        selectedBaseAsset,
        setSelectedBaseAsset,
        selectedExpiration,
        setSelectedExpiration,
        marketDataDto,
        setMarketDataDto,
        selectedDataProvider,
        setSelectedDataProvider,
        fetchMarketData,
        activeViewSection,
        setActiveViewSection,
        leftViewSectionModuleType,
        setLeftViewSectionModuleType,
        rightViewSectionModuleType,
        setRightViewSectionModuleType,
        selectedPortfolioBalance,
        setSelectedPortfolioBalance,
        selectedPortfolioVolume,
        setSelectedPortfolioVolume,
        showBalancerPriceLines,
        setShowBalancerPriceLines,
        balancerPriceLineDelta,
        setBalancerPriceLineDelta,
    };
    
    return (
        <AppContext.Provider
            value={value}
        >
            {children}
        </AppContext.Provider>        
    );
}

export const useAppContext = () => {
    const context = useContext(AppContext);
    
    if (!context) {
        throw new Error('useAppContext is undefined!');
    }
    
    return context;
}
